import React from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  estimateImportDuration,
  MAX_RANGE_FILES,
  normalizeImportRange,
} from '../utils/importRange.js';

export default function ImportRangeDialog({ open, fileCount, skipped, onConfirm, onClose }) {
  const [from, setFrom] = React.useState('0');
  const [to, setTo] = React.useState('100');
  const [committedStart, setCommittedStart] = React.useState(0);
  const [error, setError] = React.useState('');
  React.useEffect(() => {
    if (open) {
      setFrom('0');
      setTo(String(Math.min(100, Math.max(0, fileCount - 1))));
      setCommittedStart(0);
      setError('');
    }
  }, [open, fileCount]);
  let preview = null;
  try {
    preview = normalizeImportRange(from, to, fileCount, String(committedStart) !== from.trim());
  } catch {
    /* Keep partially typed input; validate on blur/submit. */
  }
  const commit = () => {
    try {
      const range = normalizeImportRange(
        from,
        to,
        fileCount,
        String(committedStart) !== from.trim()
      );
      setFrom(String(range.start));
      setTo(String(range.end));
      setCommittedStart(range.start);
      setError('');
      return range;
    } catch (rangeError) {
      setError(rangeError.message);
      return null;
    }
  };
  const handleFromChange = (event) => {
    setFrom(event.target.value);
    setError('');
  };
  const handleToChange = (event) => {
    setTo(event.target.value);
    setError('');
  };
  const handleBlur = () => {
    commit();
  };
  const handleSubmit = (event) => {
    event.preventDefault();
    const range = commit();
    if (range) onConfirm(range);
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="ecg-import-range"
      fullWidth
      maxWidth="sm"
    >
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle id="ecg-import-range">Choose SCP files to import</DialogTitle>
        <DialogContent>
          <Typography>
            {fileCount.toLocaleString()} SCP files found. Available positions: 0–
            {Math.max(0, fileCount - 1).toLocaleString()}.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 3, alignItems: 'center' }}>
            <TextField
              label="From"
              type="number"
              value={from}
              onChange={handleFromChange}
              onBlur={handleBlur}
              inputProps={{ min: 0, max: Math.max(0, fileCount - 1), step: 1 }}
              fullWidth
            />
            <Typography>to</Typography>
            <TextField
              label="To (inclusive)"
              type="number"
              value={to}
              onChange={handleToChange}
              onBlur={handleBlur}
              inputProps={{
                min: preview?.start || 0,
                max: Math.min(fileCount - 1, (preview?.start || 0) + MAX_RANGE_FILES - 1),
                step: 1,
              }}
              fullWidth
            />
          </Stack>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Typography aria-live="polite" sx={{ mt: 2 }}>
            {preview ? (
              <>
                {`${preview.count.toLocaleString()} files will be imported (positions ${preview.start.toLocaleString()}–${preview.end.toLocaleString()}).`}{' '}
                <Typography component="span" sx={{ color: 'error.main' }}>
                  Estimate time: {estimateImportDuration(preview.count)}
                </Typography>
              </>
            ) : (
              'Enter a valid range.'
            )}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Positions start at 0 and include both endpoints: 500 to 700 imports 201 files. Changing
            From sets To to From + 100 when you leave the field. A To value below From is corrected
            the same way.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Ranges are capped at the last available file and 10,000 files per import. Positions use
            folder/filename order. Only the chosen range is extracted and uploaded.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {skipped} unrelated files skipped. Nothing has been uploaded yet.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!preview}>
            Import {preview?.count.toLocaleString() || ''} files
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
ImportRangeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  fileCount: PropTypes.number.isRequired,
  skipped: PropTypes.number.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
